import { Audience_Status, AudienceFilter, AudienceFragment, OfferFragment } from '@occc/graphql';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';
import { DirtyFieldsType, Operation } from '@occc-app/offerHelpers/types';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import { makeCreateInAudienceConstraintOperation } from '@occc-app/offerHelpers/operations/constraints';
import { useGetAudiencesLazy } from '@occc-app/dataAccessLayer/audience/queries';

import { InAudienceConstraintFormValues, Audience } from './types';

export const mapAudiencesQueryResponseToRawArray = (
  response: ReturnType<typeof useGetAudiencesLazy>['response'],
): Audience[] =>
  response.data
    ? response.data.map(({ uuid, name, status, hasBeenBackFilled, targetingAttributes }) => ({
        uuid,
        name,
        status,
        hasBeenBackFilled,
        targetingAttributes,
      }))
    : [];

export const mapRawAudiencesResponseItemToAutocompleteOption = (item: Audience) => ({
  value: item.uuid,
  label: item.name,
  _raw: item,
});

export const makeAudienceFilter =
  (chainId: string) =>
  (value?: string): AudienceFilter => ({
    statusContains: [Audience_Status.Active],
    search: value ? value : undefined,
    ...(chainId ? { chainIdEquals: chainId } : {}),
  });

export const getInAudienceConstraint = (offer: OfferFragment | null | undefined) => {
  return offer?.constraints.find(constraintHelper.isInAudience);
};

export const getInAudienceConstraintInitialValues = (
  offer: OfferFragment | null | undefined,
  audience?: AudienceFragment,
) => {
  const inAudienceConstraint = getInAudienceConstraint(offer);

  return {
    inAudience:
      inAudienceConstraint && audience?.uuid === inAudienceConstraint.audienceId
        ? {
            id: inAudienceConstraint.id,
            audience: {
              uuid: audience.uuid,
              membershipCount: audience.membershipCount,
              name: audience.name,
              status: audience?.status,
              hasBeenBackFilled: audience?.hasBeenBackFilled,
              targetingAttributes: audience?.targetingAttributes,
            },
          }
        : {},
  };
};

export const getInAudienceConstraintOperations = (
  offerId: string,
  values: InAudienceConstraintFormValues,
  dirtyFields: DirtyFieldsType,
): Operation[] => {
  const operations: Operation[] = [];

  if (isFieldDirty<InAudienceConstraintFormValues>(dirtyFields, 'inAudience.audience')) {
    if (values.inAudience?.id) {
      operations.push(makeDeleteOperation('constraintDelete', values.inAudience.id));
    }
    if (values.inAudience?.audience?.uuid) {
      operations.push(
        makeCreateInAudienceConstraintOperation(offerId, values.inAudience.audience?.uuid),
      );
    }
  }

  return operations;
};
